<template>
  <div v-if="items.length" class="page-subnav-wrap">
    <nav class="page-subnav-breadcrumb" aria-label="Навигационная цепочка">
      <ol class="page-subnav-breadcrumb__list">
        <li class="page-subnav-breadcrumb__item">
          <a :href="homeHref" class="page-subnav-breadcrumb__link">Главная</a>
        </li>
        <li
          v-if="simpleBreadcrumb"
          class="page-subnav-breadcrumb__item page-subnav-breadcrumb__item--current"
          aria-current="page"
        >
          <span class="page-subnav-breadcrumb__current">{{ sectionTitle }}</span>
        </li>
        <template v-else>
          <li class="page-subnav-breadcrumb__item">
            <a :href="sectionHref" class="page-subnav-breadcrumb__link">{{ sectionTitle }}</a>
          </li>
          <li
            class="page-subnav-breadcrumb__item page-subnav-breadcrumb__item--current"
            aria-current="page"
          >
            <span class="page-subnav-breadcrumb__current">{{ currentPageLabel }}</span>
          </li>
        </template>
      </ol>
    </nav>
    <h3 v-if="sectionTitle" class="page-subnav__title">{{ sectionTitle }}</h3>
    <nav class="page-subnav" :aria-label="sectionTitle || 'Дополнительная навигация по разделу'">
      <ul class="page-subnav__list">
        <li v-for="(item, i) in items" :key="i" class="page-subnav__item">
          <a
            :ref="el => setActiveLink(el as Element, item)"
            :href="item.href"
            class="page-subnav__link"
            :class="{ 'page-subnav__link--active': isActive(item) }"
            >{{ item.label }}</a
          >
        </li>
      </ul>
    </nav>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, nextTick, onMounted, watch } from 'vue';
import type { SubnavItem } from './subnav-config';

const props = defineProps<{
  items: SubnavItem[];
  activeHref?: string;
  sectionTitle: string;
  homeHref: string;
  sectionHref: string;
  simpleBreadcrumb?: boolean;
}>();

const activeLink = ref<HTMLAnchorElement | null>(null);

const currentPageLabel = computed(() => {
  const item = props.items.find((i) => i.href === props.activeHref);
  return item?.label ?? props.sectionTitle;
});

function isActive(item: SubnavItem): boolean {
  if (!props.activeHref) return false;
  return item.href === props.activeHref;
}

function setActiveLink(el: Element | null, item: SubnavItem) {
  if (isActive(item) && el instanceof HTMLAnchorElement) {
    activeLink.value = el;
  }
}

function scrollToActive() {
  nextTick(() => {
    activeLink.value?.scrollIntoView({
      behavior: 'smooth',
      inline: 'center',
      block: 'nearest',
    });
  });
}

onMounted(scrollToActive);

watch(() => props.activeHref, scrollToActive);
</script>

<style scoped>
.page-subnav-breadcrumb {
  font-family: var(--stc-font-medium), sans-serif;
  font-weight: 500;
  font-size: 10px;
  line-height: 1.4;
  letter-spacing: 0%;
  color: var(--stc-gray-300);
}

.page-subnav-breadcrumb__list {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  list-style: none;
  margin: 0;
  padding: 0;
  font-size: 10px;
  line-height: 1.4;
}

.page-subnav-breadcrumb__item {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.page-subnav-breadcrumb__item:not(:first-child)::before {
  display: inline;
  content: '→';
  width: 10px;
  color: var(--stc-gray-300);
  pointer-events: none;
  font-weight: 500;
  font-family: var(--stc-font-medium), sans-serif;
  font-size: 10px;
  line-height: 1.4;
}

.page-subnav-breadcrumb__link {
  color: var(--stc-gray-300);
  text-decoration: none;
  transition: color 0.15s ease;
  font-size: 10px;
  line-height: 1.4;
  letter-spacing: 0%;
}

.page-subnav-breadcrumb__link:hover {
  text-decoration: underline;
}

.page-subnav-breadcrumb__current {
  font-size: 10px;
}

.page-subnav__title {
  margin-top: 24px;
  color: #151515;
  font-variant-numeric: lining-nums proportional-nums;
  font-feature-settings: 'liga' off;
  font-family: Involve;
  font-size: 29px;
  font-weight: 600;
  line-height: 137%;
}

.page-subnav-wrap {
  padding-top: 40px;
  padding-bottom: 0;
  border-bottom: 1px solid rgba(233, 233, 233, 0.9);
}

.page-subnav {
  margin-top: 20px;
  background: transparent;
}

.page-subnav__list {
  display: flex;
  flex-wrap: wrap;
  gap: 24px;
  list-style: none;
  margin: 0;
  padding: 0;
}

.page-subnav__item {
  margin: 0;
  flex-shrink: 0;
}

.page-subnav__link {
  text-wrap: nowrap;
  white-space: nowrap;
  display: inline-block;
  position: relative;
  padding: 20px 0;
  font-family: var(--stc-font-medium), sans-serif;
  font-weight: 500;
  font-size: 14px;
  line-height: 1.6;
  letter-spacing: 0%;
  color: var(--stc-gray-400);
  text-decoration: none;
  transition: color 0.25s ease;
}

.page-subnav__link::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  width: 100%;
  height: 2px;
  background: var(--stc-primary-color);
  transform: scaleX(0);
  transform-origin: center;
  transition: transform 0.3s ease;
  will-change: transform;
}

.page-subnav__link:hover::after {
  transform: scaleX(1);
}

.page-subnav__link--active {
  font-family: var(--stc-font-semibold), sans-serif;
  font-weight: 600;
  color: var(--stc-primary-color);
}

.page-subnav__link--active::after {
  transform: scaleX(1);
}

@media (max-width: 1024px) {
  .page-subnav-wrap {
    padding-top: 28px;
  }

  .page-subnav__list {
    gap: 16px;
    flex-wrap: nowrap;
  }

  .page-subnav__item {
    display: flex;
    min-width: 0;
  }

  .page-subnav__link {
    display: flex;
    align-items: center;
    text-align: center;
    justify-content: center;
    width: 100%;
    padding: 16px 0;
    box-sizing: border-box;
  }

  .page-subnav__title {
    font-size: 24px;
    margin-top: 18px;
  }
}

@media (max-width: 992px) {
  .page-subnav {
    overflow-x: scroll;
    -ms-overflow-style: none; /* IE и Edge */
    scrollbar-width: none; /* Firefox */
    &::-webkit-scrollbar {
      display: none; /* Chrome, Safari, Opera */
    }
  }

  .page-subnav__list {
    display: flex;
    flex-wrap: nowrap;
  }

  .page-subnav__item {
    flex-grow: 1;
    flex-shrink: 0;
  }
}

@media (max-width: 768px) {
  .page-subnav-wrap {
    padding: 18px 18px 0 18px;
  }

  .page-subnav__title {
    font-size: 18px;
    margin-top: 17px;
  }
}

@media (max-width: 576px) {
  .page-subnav__link {
    font-size: 13px;
  }
}
</style>
